/**
 * useCanListAllPatientTreatments — mirrors the treatments read rule for the
 * "unfiltered list" query: an unscoped `getTreatmentsForPatient` is only
 * allowed by firestore.rules for admins / treatments.viewAllClinic /
 * shared-treatments clinics / the patient's primary therapist / a co-therapist
 * with treatments.viewSharedPatient. Everyone else must scope the query to
 * their own reports (otherwise permission-denied CK-7100 spam).
 *
 * Used by PatientProfilePage (treatments tab) and AiSummaryPage so both hit
 * the same RTK cache entry.
 */

import { useSelector } from "react-redux";
import {
  selectTherapistId,
  selectIsAdmin,
  selectHasPermission,
  selectAllowMultipleTherapists,
  selectClinicSharesTreatments,
} from "../store/slices/authSlice";

export default function useCanListAllPatientTreatments(patient) {
  const currentTherapistId = useSelector(selectTherapistId);
  const isAdmin = useSelector(selectIsAdmin);
  const canViewAllTreatments = useSelector(selectHasPermission("treatments.viewAllClinic"));
  const canViewSharedTreatments = useSelector(selectHasPermission("treatments.viewSharedPatient"));
  const allowMultipleTherapists = useSelector(selectAllowMultipleTherapists);
  const sharesTreatments = useSelector(selectClinicSharesTreatments);

  const isPrimaryTherapist = !!patient?.therapistId && patient.therapistId === currentTherapistId;
  const isCoTherapistOfPatient =
    Array.isArray(patient?.therapistIds) && patient.therapistIds.includes(currentTherapistId);

  return (
    isAdmin ||
    canViewAllTreatments ||
    sharesTreatments ||
    isPrimaryTherapist ||
    (allowMultipleTherapists && isCoTherapistOfPatient && canViewSharedTreatments !== false)
  );
}
